<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-s-data" title="人员离职风险指标横向对比" subtitle="对比三名员工的行为差异，制定针对性的保留策略" />

    <div class="compare-selectors">
      <div class="compare-selector">
        <div class="selector-label">员工 A</div>
        <el-select v-model="employeeA" placeholder="选择员工A" class="selector-input" @change="onChange">
          <el-option
            v-for="e in availableEmployees"
            :key="e.employee_id"
            :label="e.real_name + ' (' + (e.job_title || '-') + ')'"
            :value="e.employee_id"
            :disabled="isSelected(e.employee_id, 'A')"
          />
        </el-select>
      </div>
      <div class="compare-selector">
        <div class="selector-label">员工 B</div>
        <el-select v-model="employeeB" placeholder="选择员工B" class="selector-input" @change="onChange">
          <el-option
            v-for="e in availableEmployees"
            :key="e.employee_id"
            :label="e.real_name + ' (' + (e.job_title || '-') + ')'"
            :value="e.employee_id"
            :disabled="isSelected(e.employee_id, 'B')"
          />
        </el-select>
      </div>
      <div class="compare-selector">
        <div class="selector-label">员工 C</div>
        <el-select v-model="employeeC" placeholder="选择员工C" class="selector-input" @change="onChange">
          <el-option
            v-for="e in availableEmployees"
            :key="e.employee_id"
            :label="e.real_name + ' (' + (e.job_title || '-') + ')'"
            :value="e.employee_id"
            :disabled="isSelected(e.employee_id, 'C')"
          />
        </el-select>
      </div>
    </div>

    <div v-if="hasSelection" class="compare-scores">
      <div v-for="(emp, idx) in selectedEmployees" :key="idx" class="score-box" :class="'score-box-' + idx">
        <div class="score-box-value">{{ emp.score }}</div>
        <div class="score-box-name">{{ emp.name }}</div>
        <div class="score-box-tag" :class="'tag-' + emp.riskClass">{{ emp.riskText }}</div>
        <div class="score-box-time">评估时间：{{ emp.evalTime }}</div>
      </div>
    </div>

    <div v-if="comparison.length" class="compare-table-wrapper">
      <el-table :data="comparison" class="compare-table" :row-class-name="rowClassName">
        <el-table-column label="行为分类" width="140" align="left">
          <template slot-scope="scope">
            <span class="category-cell">{{ getCategoryName(scope.row.category_id) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="行为描述" align="left" min-width="280">
          <template slot-scope="scope">
            <span class="behavior-desc-cell">{{ scope.row.description }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="employeeA" :label="getEmployeeName(employeeA)" width="140" align="center">
          <template slot-scope="scope">
            <el-tag :type="answerTagType(scope.row.employee_a_answer)" size="small" effect="dark">
              {{ answerTagText(scope.row.employee_a_answer) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="employeeB" :label="getEmployeeName(employeeB)" width="140" align="center">
          <template slot-scope="scope">
            <el-tag :type="answerTagType(scope.row.employee_b_answer)" size="small" effect="dark">
              {{ answerTagText(scope.row.employee_b_answer) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="employeeC" :label="getEmployeeName(employeeC)" width="140" align="center">
          <template slot-scope="scope">
            <el-tag :type="answerTagType(scope.row.employee_c_answer)" size="small" effect="dark">
              {{ answerTagText(scope.row.employee_c_answer) }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <EmptyState v-else icon="el-icon-s-data" title="请选择至少两名员工进行对比" hint="选择上方下拉框中的员工，系统将自动生成行为差异对比表" />
  </div>
</template>

<script>
import PageHeader from '../components/PageHeader.vue'
import EmptyState from '../components/EmptyState.vue'

export default {
  name: 'ABCompare',
  components: { PageHeader, EmptyState },
  data() {
    return {
      employeeA: null,
      employeeB: null,
      employeeC: null,
      comparison: []
    }
  },
  computed: {
    thresholds() {
      return this.$store.state.settings.settings.thresholds || { lowMax: 4, mediumMax: 10 }
    },
    employees() {
      const user = this.$store.state.user
      const email = user && user.email ? user.email : ''
      let list = []
      if (email) {
        list = this.$store.getters.subordinatesByEmail(email) || []
      } else {
        list = this.$store.getters.taskEmployees || []
      }
      return list.filter(e => e.employment_status === 1)
    },
    behaviors() {
      return this.$store.state.behaviors.behaviors || []
    },
    categories() {
      return this.$store.state.behaviors.categories || []
    },
    availableEmployees() {
      // AB 对比只显示已评估的员工，未评估的没有数据无法对比
      return this.employees.filter(e => e.latest_risk_level && e.latest_score > 0)
    },
    hasSelection() {
      return this.employeeA || this.employeeB || this.employeeC
    },
    selectedEmployees() {
      const ids = [this.employeeA, this.employeeB, this.employeeC].filter(Boolean)
      return ids.map(id => {
        const e = this.employees.find(emp => emp.employee_id === id)
        if (!e) return { name: '-', score: 0, riskClass: 'low', riskText: '低风险', evalTime: '-' }
        const hasEval = e.latest_risk_level && e.latest_score > 0
        if (!hasEval) {
          return { name: e.real_name, score: '-', riskClass: 'low', riskText: '未评估', evalTime: '-' }
        }
        const score = e.latest_score
        const level = e.latest_risk_level
        return {
          name: e.real_name,
          score,
          riskClass: level === 3 ? 'high' : (level === 2 ? 'medium' : 'low'),
          riskText: level === 3 ? '高风险' : (level === 2 ? '中风险' : '低风险'),
          evalTime: new Date().toISOString().slice(0, 10)
        }
      })
    }
  },
  methods: {
    isSelected(employeeId, currentSlot) {
      if (currentSlot !== 'A' && this.employeeA === employeeId) return true
      if (currentSlot !== 'B' && this.employeeB === employeeId) return true
      if (currentSlot !== 'C' && this.employeeC === employeeId) return true
      return false
    },
    getEmployeeName(employeeId) {
      const e = this.employees.find(emp => emp.employee_id === employeeId)
      return e ? e.real_name : '-'
    },
    getCategoryName(categoryId) {
      const cat = this.categories.find(c => c.category_id === categoryId)
      return cat ? cat.category_name : '-'
    },
    getLatestEvaluation(employeeId) {
      const list = (this.$store.state.evaluations || [])
        .filter(e => e.employee_id === employeeId)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      return list[0] || null
    },
    getAnswer(evalRecord, behaviorId) {
      if (!evalRecord) return null
      // 优先使用 answers 对象（真实数据结构）
      if (evalRecord.answers && evalRecord.answers[behaviorId] !== undefined) {
        return evalRecord.answers[behaviorId]
      }
      // 兼容 matched_behaviors 数组（旧 mock 数据结构）
      if (evalRecord.matched_behaviors) {
        const matched = evalRecord.matched_behaviors.find(b => b.behavior_id === behaviorId)
        if (matched) return 1
      }
      return null
    },
    onChange() {
      this.generateComparison()
    },
    generateComparison() {
      const selected = [this.employeeA, this.employeeB, this.employeeC].filter(Boolean)
      if (selected.length < 2) {
        this.comparison = []
        return
      }

      // 获取每个选中员工的最新评估记录
      const evalA = this.employeeA ? this.getLatestEvaluation(this.employeeA) : null
      const evalB = this.employeeB ? this.getLatestEvaluation(this.employeeB) : null
      const evalC = this.employeeC ? this.getLatestEvaluation(this.employeeC) : null

      this.comparison = this.behaviors.map(b => ({
        behavior_id: b.behavior_id,
        category_id: b.category_id,
        description: b.description,
        employee_a_answer: this.getAnswer(evalA, b.behavior_id),
        employee_b_answer: this.getAnswer(evalB, b.behavior_id),
        employee_c_answer: this.getAnswer(evalC, b.behavior_id)
      }))
    },
    answerTagType(answer) {
      if (answer === 1) return 'danger'
      if (answer === 2) return 'success'
      return ''  // 未匹配时不显示标签样式
    },
    answerTagText(answer) {
      if (answer === 1) return '符合'
      if (answer === 2) return '不符合'
      return '-'  // 该行为未匹配，不是员工未评估
    },
    rowClassName({ row }) {
      // 差异高亮：已评估员工中任意两人不一致则高亮
      const answers = []
      if (row.employee_a_answer !== undefined && row.employee_a_answer !== null) answers.push(row.employee_a_answer)
      if (row.employee_b_answer !== undefined && row.employee_b_answer !== null) answers.push(row.employee_b_answer)
      if (row.employee_c_answer !== undefined && row.employee_c_answer !== null) answers.push(row.employee_c_answer)
      const unique = [...new Set(answers)]
      return unique.length > 1 ? 'highlight-row' : ''
    }
  }
}
</script>

<style scoped>
.page-container { max-width: 1200px; margin: 0 auto; }

.compare-selectors {
  display: flex;
  align-items: flex-end;
  gap: var(--space-lg);
  margin-bottom: var(--space-2xl);
}

.compare-selector { flex: 1; }

.selector-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-sm);
}

.selector-input { width: 100%; }

.compare-scores {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-xl);
  margin-bottom: var(--space-2xl);
}

.score-box {
  flex: 1;
  text-align: center;
  padding: var(--space-2xl);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  transition: all var(--duration-normal) var(--ease-out);
}

.score-box:hover { box-shadow: var(--shadow-md); }

.score-box-value {
  font-size: 48px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: var(--space-sm);
}

.score-box-0 .score-box-value { color: var(--color-high); }
.score-box-1 .score-box-value { color: var(--color-medium); }
.score-box-2 .score-box-value { color: var(--color-primary); }

.score-box-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: var(--space-sm);
}

.score-box-tag {
  display: inline-block;
  padding: 4px 14px;
  border-radius: var(--radius-full);
  font-size: 12px;
  font-weight: 600;
}

.tag-high { background: var(--color-high-bg); color: var(--color-high-text); }
.tag-medium { background: var(--color-medium-bg); color: var(--color-medium-text); }
.tag-low { background: var(--color-low-bg); color: var(--color-low-text); }

.score-box-time {
  margin-top: var(--space-sm);
  font-size: 12px;
  color: var(--color-text-muted);
}

.compare-table-wrapper {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.compare-table >>> .highlight-row { background: var(--color-bg) !important; }

.behavior-desc-cell {
  font-weight: 500;
  color: var(--color-text);
}

.category-cell {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
}
</style>
